import {
  BoxGeometry,
  CatmullRomCurve3,
  CylinderGeometry,
  Group,
  Mesh,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  PlaneGeometry,
  RepeatWrapping,
  TubeGeometry,
  Vector3,
} from 'three';
import type { CanvasTexture } from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { t } from '../../i18n/i18n';
import { drawnTexture } from './canvasTexture';

/** Half the ring's width inside the ropes, in metres: a 6 m ring. */
export const ringHalf = 3;
const postHeight = 1.55;
const ropeHeights = [0.45, 0.8, 1.15, 1.5];
const ropeColours = [0xd62d2d, 0xf2f2f2, 0x2a52d8, 0xd62d2d];

/**
 * The ring: its printed canvas, the apron and skirt, four corner posts under tall padded covers in the corners'
 * colours, and four sagging ropes tied together half way along each side.
 */
export function buildRing(): Group {
  const ring = new Group();
  ring.name = 'Ring';

  const canvasPrint = drawnTexture(2048, 2048, (g, w, h) => {
    const blue = g.createRadialGradient(w / 2, h / 2, w * 0.05, w / 2, h / 2, w * 0.75);
    blue.addColorStop(0, '#2f6fe8');
    blue.addColorStop(1, '#173c9c');
    g.fillStyle = blue;
    g.fillRect(0, 0, w, h);
    // Worn canvas: faint scuffs where the fighters move.
    for (let i = 0; i < 900; i++) {
      g.fillStyle = `rgba(255,255,255,${Math.random() * 0.03})`;
      const r = 4 + Math.random() * 40;
      g.beginPath();
      g.ellipse(w * (0.2 + Math.random() * 0.6), h * (0.2 + Math.random() * 0.6), r, r * 0.3, Math.random() * Math.PI, 0, Math.PI * 2);
      g.fill();
    }
    g.strokeStyle = 'rgba(255,255,255,0.9)';
    g.lineWidth = 26;
    g.strokeRect(60, 60, w - 120, h - 120);
    g.lineWidth = 10;
    g.beginPath();
    g.arc(w / 2, h / 2, w * 0.2, 0, Math.PI * 2);
    g.stroke();
    g.fillStyle = 'rgba(255,255,255,0.92)';
    g.font = `900 ${w * 0.11}px "Russo One", sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(t('glove-label'), w / 2, h / 2 - w * 0.02);
    g.font = `700 ${w * 0.03}px "Russo One", sans-serif`;
    g.fillText(t('game-subtitle'), w / 2, h / 2 + w * 0.08);
  });
  const canvasMaterial = new MeshStandardMaterial({ map: canvasPrint, roughness: 0.82, metalness: 0 });
  const floor = new Mesh(new PlaneGeometry(ringHalf * 2 + 1.2, ringHalf * 2 + 1.2), canvasMaterial);
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  ring.add(floor);

  const apronPrint = drawnTexture(1024, 64, (g, w, h) => {
    g.fillStyle = '#101826';
    g.fillRect(0, 0, w, h);
    g.fillStyle = '#e8c35a';
    g.font = `700 ${h * 0.55}px "Russo One", sans-serif`;
    g.textBaseline = 'middle';
    for (let x = 20; x < w; x += 360) g.fillText(`${t('game-title')} ★ ${t('game-subtitle')}`, x, h / 2);
  });
  apronPrint.wrapS = RepeatWrapping;
  const skirtMaterial = new MeshStandardMaterial({ map: apronPrint, roughness: 0.6 });
  const skirtSize = ringHalf * 2 + 1.2;
  for (let side = 0; side < 4; side++) {
    const skirt = new Mesh(new PlaneGeometry(skirtSize, 1.1), skirtMaterial);
    const angle = (side * Math.PI) / 2;
    skirt.position.set(Math.sin(angle) * (skirtSize / 2), -0.55, Math.cos(angle) * (skirtSize / 2));
    skirt.rotation.y = angle;
    ring.add(skirt);
  }

  const postMaterial = new MeshPhysicalMaterial({ color: 0xb8bcc4, metalness: 0.9, roughness: 0.28 });
  const cornerColours = ['#d62d2d', '#f2f2f2', '#2a52d8', '#f2f2f2'];
  const cushionShape = new RoundedBoxGeometry(0.3, 1.36, 0.3, 4, 0.1);
  const corners = cornerPositions();
  corners.forEach((corner, i) => {
    const post = new Mesh(new CylinderGeometry(0.06, 0.07, postHeight + 0.2, 16), postMaterial);
    post.position.set(corner.x, (postHeight + 0.2) / 2 - 0.1, corner.z);
    post.castShadow = true;
    ring.add(post);
    const cushion = new Mesh(cushionShape, new MeshPhysicalMaterial({ map: cornerPrint(cornerColours[i]), roughness: 0.42, clearcoat: 0.6, clearcoatRoughness: 0.3 }));
    cushion.position.set(corner.x * 0.985, 0.97, corner.z * 0.985);
    cushion.rotation.y = Math.PI / 4;
    cushion.castShadow = true;
    ring.add(cushion);
  });

  ropeHeights.forEach((height, i) => ring.add(rope(height, ropeColours[i])));
  // A strap half way along each side ties the ropes together, as on a real ring.
  const strapMaterial = new MeshStandardMaterial({ color: 0x17171c, roughness: 0.7 });
  const strapHeight = ropeHeights[ropeHeights.length - 1] - ropeHeights[0] + 0.08;
  for (let side = 0; side < 4; side++) {
    const angle = (side * Math.PI) / 2;
    const strap = new Mesh(new BoxGeometry(0.07, strapHeight, 0.07), strapMaterial);
    const out = ringHalf + 0.1;
    strap.position.set(Math.sin(angle) * out, (ropeHeights[0] + ropeHeights[ropeHeights.length - 1]) / 2 - 0.05, Math.cos(angle) * out);
    strap.rotation.y = angle;
    ring.add(strap);
  }
  return ring;
}

// The print on a corner's padded cover: its colour, a seam near each end, and the fight's roundel.
function cornerPrint(colour: string): CanvasTexture {
  return drawnTexture(256, 1024, (g, w, h) => {
    g.fillStyle = colour;
    g.fillRect(0, 0, w, h);
    g.fillStyle = 'rgba(0,0,0,0.12)';
    for (const y of [h * 0.08, h * 0.92]) g.fillRect(0, y, w, 10);
    const light = colour === '#f2f2f2';
    g.beginPath();
    g.arc(w / 2, h * 0.3, w * 0.36, 0, Math.PI * 2);
    g.fillStyle = light ? '#15204a' : '#ffffff';
    g.fill();
    g.fillStyle = light ? '#ffffff' : colour;
    g.font = `900 ${w * 0.3}px "Russo One", sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(t('glove-label'), w / 2, h * 0.3 + w * 0.02);
  });
}

function cornerPositions(): Vector3[] {
  const c = ringHalf + 0.1;
  return [new Vector3(-c, 0, -c), new Vector3(c, 0, -c), new Vector3(c, 0, c), new Vector3(-c, 0, c)];
}

// One rope all the way round, sagging a little between the posts.
function rope(height: number, colour: number): Mesh {
  const corners = cornerPositions();
  const points: Vector3[] = [];
  for (let side = 0; side < 4; side++) {
    const from = corners[side];
    const to = corners[(side + 1) % 4];
    for (let i = 0; i < 12; i++) {
      const t = i / 12;
      const point = from.clone().lerp(to, t);
      point.y = height - Math.sin(t * Math.PI) * 0.05;
      points.push(point);
    }
  }
  const curve = new CatmullRomCurve3(points, true, 'catmullrom', 0.2);
  const material = new MeshPhysicalMaterial({ color: colour, roughness: 0.35, clearcoat: 0.8, clearcoatRoughness: 0.2 });
  const mesh = new Mesh(new TubeGeometry(curve, 240, 0.028, 10, true), material);
  mesh.castShadow = true;
  return mesh;
}
